import { useTranslation } from "react-i18next";
import { useUploadJobStatus } from "@noa/api-client";
import type { FileRecord, FileStatus, JobPhase, StaleRemoval, SyncProgress } from "@noa/api-core";
import { JobPhaseTag } from "./job-phase-tag";
import { relaunchValues, type SharePointSyncForm } from "~/utils/sharepoint-sync-form";
import { type RemovalNotice, staleRemovalNotice } from "~/utils/stale-removal";
import { ingestBarStatus, ingestedSoFar, onlyFailedFilesLeft, sectorCounts, syncOutcome } from "~/utils/sync-progress";
import {
  Alert,
  Button,
  Card,
  Paragraph,
  Progress,
  Tag,
  Text,
  Title,
  type TagColor,
  DataTable,
  type DataTableColumn,
} from "~/components/ui";

type UploadJobProgressProps = {
  jobId: string;
  onNewUpload: () => void;
  /** Opens the SharePoint sync form, filled with the settings of the sync to run again. */
  onRelaunchSync?: (values: SharePointSyncForm, fromJobId: string) => void;
};

const FILE_STATUS_CONFIG: Record<FileStatus, { color: TagColor }> = {
  pending: { color: "default" },
  fetching: { color: "processing" },
  fetched: { color: "cyan" },
  ingesting: { color: "processing" },
  ingested: { color: "success" },
  skipped: { color: "warning" },
  failed: { color: "error" },
};

type IncrementalSyncDetailsProps = {
  sync: SyncProgress;
  phase: JobPhase;
  failed: number;
  /** The failed files and why, when no alert above shows them already. */
  failures?: string | null;
};

function IncrementalSyncDetails({ sync, phase, failed, failures }: IncrementalSyncDetailsProps) {
  const { t, i18n } = useTranslation("admin");
  // A failed run keeps its counts too: what it loaded is in Qdrant.
  const ended = phase === "completed" || phase === "failed";
  const formatNumber = (value: number) => value.toLocaleString(i18n.language);
  const sectors = sectorCounts(sync.pending_by_sector)
    .map(({ sector, count }) => t("uploadAdmin.progress.incremental.sector", { sector, files: formatNumber(count) }))
    .join(" · ");

  return (
    <div className="flex flex-col gap-0.5" data-testid="incremental-sync">
      <Text strong>{t("uploadAdmin.progress.incremental.title")}</Text>
      <Text>
        {ended
          ? t("uploadAdmin.progress.incremental.ingested", {
              ingested: formatNumber(sync.processed ?? 0),
              remaining: formatNumber(sync.remaining),
            })
          : t("uploadAdmin.progress.incremental.remainingAtStart", { remaining: formatNumber(sync.remaining) })}
      </Text>
      {ended && failed > 0 && (
        <Text tone="warning">
          {t("uploadAdmin.progress.incremental.failed", { count: failed, files: formatNumber(failed) })}
        </Text>
      )}
      {ended && failed > 0 && failures && (
        <Paragraph tone="muted" className="mb-0 max-h-24 overflow-y-auto text-xs whitespace-pre-wrap">
          {failures}
        </Paragraph>
      )}
      {phase === "failed" && <Text tone="muted">{t("uploadAdmin.progress.incremental.keptOnFailure")}</Text>}
      {sectors && <Text tone="muted">{t("uploadAdmin.progress.incremental.bySector", { sectors })}</Text>}
    </div>
  );
}

type StaleRemovalAlertProps = {
  removal: StaleRemoval;
  notice: RemovalNotice;
};

/** What the sync did with the stored documents its source no longer lists. */
function StaleRemovalAlert({ removal, notice }: StaleRemovalAlertProps) {
  const { t, i18n } = useTranslation("admin");
  const formatNumber = (value: number | null | undefined) => (value ?? 0).toLocaleString(i18n.language);
  const values = {
    documents: formatNumber(removal.removed),
    chunks: formatNumber(removal.chunks_removed),
    images: formatNumber(removal.images_removed),
    left: formatNumber(removal.images_left),
    stale: formatNumber(removal.stale),
    stored: formatNumber(removal.stored),
    percent: formatNumber(removal.max_percent),
    floor: formatNumber(removal.floor),
    reason: t(`uploadAdmin.progress.removal.gap.${removal.gap ?? "not_declared"}`),
  };
  const details: Partial<Record<RemovalNotice["kind"], string>> = {
    removed: [
      t("uploadAdmin.progress.removal.removedDetails", values),
      removal.images_left
        ? t("uploadAdmin.progress.removal.imagesLeft", { ...values, count: removal.images_left })
        : "",
    ]
      .filter(Boolean)
      .join(" "),
    blocked: t("uploadAdmin.progress.removal.blockedDetails", values),
  };

  return (
    <Alert
      type={notice.type}
      message={t(`uploadAdmin.progress.removal.${notice.kind}`, { ...values, count: removal.removed ?? 0 })}
      description={details[notice.kind]}
      showIcon
      data-testid="stale-removal"
    />
  );
}

export function UploadJobProgress({ jobId, onNewUpload, onRelaunchSync }: UploadJobProgressProps) {
  const { t, i18n } = useTranslation("admin");
  const { status, isLoading, isError, isTerminal } = useUploadJobStatus(jobId, { includeFiles: true });

  // A failed poll keeps the last status on screen; only a job never loaded ends here.
  if (isError && !status) {
    return (
      <Card>
        <div className="flex flex-col gap-2">
          <Alert type="error" message={t("uploadAdmin.progress.loadError")} showIcon />
          <Button onClick={onNewUpload}>{t("uploadAdmin.actions.newUpload")}</Button>
        </div>
      </Card>
    );
  }

  if (isLoading || !status) {
    return (
      <Card loading>
        <p>{t("common:asyncStatus.loading")}</p>
      </Card>
    );
  }

  const fetchProgress = status.fetch_progress;
  const ingestProgress = status.ingest_progress;

  const fetchPercent =
    fetchProgress && fetchProgress.total > 0
      ? Math.round(((fetchProgress.completed + fetchProgress.skipped) / fetchProgress.total) * 100)
      : 0;

  const incremental = status.sync_progress != null;
  const ingestDone = ingestProgress ? ingestedSoFar(ingestProgress, { ended: isTerminal, incremental }) : 0;
  const ingestPercent =
    ingestProgress && ingestProgress.total > 0 ? Math.round((ingestDone / ingestProgress.total) * 100) : 0;
  const ingestLabel = isTerminal && !incremental ? (ingestProgress?.completed ?? 0) : ingestDone;
  const formatNumber = (value: number) => value.toLocaleString(i18n.language);
  const fetchText = fetchProgress
    ? `${formatNumber(fetchProgress.completed)}/${formatNumber(fetchProgress.total)}`
    : "";
  const ingestText = ingestProgress ? `${formatNumber(ingestLabel)}/${formatNumber(ingestProgress.total)}` : "";
  const ingestFailed = ingestProgress?.failed ?? 0;
  const ingestValueText =
    ingestFailed > 0
      ? t("uploadAdmin.progress.barWithFailures", { progress: ingestText, files: formatNumber(ingestFailed) })
      : ingestText;

  const outcome = syncOutcome(status.phase, status.sync_progress);
  const removalNotice = staleRemovalNotice(status.stale_removal);
  const partialMessageKey =
    status.sync_progress?.stop_reason === "deadline"
      ? "uploadAdmin.progress.incremental.partialDeadline"
      : "uploadAdmin.progress.incremental.partial";
  const failedOnly =
    outcome === "partial" &&
    status.sync_progress != null &&
    onlyFailedFilesLeft(status.sync_progress, ingestProgress?.failed ?? 0);

  // A finished SharePoint sync runs again when it failed, or left or failed files; an
  // incremental one picks up where it stopped. One that failed before its discovery ended
  // has no sync_progress yet, so this does not ask for it.
  const finishedClean = status.phase === "completed" && outcome !== "partial" && ingestFailed === 0;
  const canRelaunch = onRelaunchSync != null && isTerminal && !finishedClean && status.source_type === "sharepoint";
  const relaunch = () => onRelaunchSync?.(relaunchValues(status), status.job_id);
  // The alerts below carry the button; without one of them, it sits next to "New upload".
  const relaunchInAlert = outcome === "partial" || (status.phase === "failed" && Boolean(status.error));
  const relaunchAction = canRelaunch ? (
    <Button size="small" variant="primary" onClick={relaunch} data-testid="relaunch-sync-button">
      {t("uploadAdmin.progress.incremental.relaunch")}
    </Button>
  ) : undefined;

  const fileColumns: DataTableColumn<FileRecord>[] = [
    {
      title: t("uploadAdmin.progress.files"),
      key: "filename",
      ellipsis: true,
      render: ({ filename }) => <code className="text-xs">{filename}</code>,
    },
    {
      title: t("uploadAdmin.progress.status"),
      key: "status",
      width: 120,
      render: ({ status: fileStatus }) => (
        <Tag color={(fileStatus && FILE_STATUS_CONFIG[fileStatus]?.color) || "default"}>
          {t(`uploadAdmin.progress.phase.${fileStatus}`, { defaultValue: fileStatus })}
        </Tag>
      ),
    },
    {
      title: t("common:error"),
      key: "error",
      ellipsis: true,
      render: ({ error }) => error && <Text tone="danger">{error}</Text>,
    },
  ];

  return (
    <Card>
      <div className="flex w-full flex-col gap-6">
        <div className="flex items-center justify-between">
          <Title level={4} className="!mb-0">
            {t("uploadAdmin.progress.title")}
          </Title>
          <JobPhaseTag
            phase={status.phase}
            sourceType={status.source_type ?? "blob"}
            syncProgress={status.sync_progress}
          />
        </div>

        <Text tone="muted">
          {t("uploadAdmin.progress.jobId")}: <code>{jobId}</code>
        </Text>

        {status.phase === "failed" && status.error && (
          <Alert type="error" message={status.error} action={relaunchAction} showIcon />
        )}

        {status.phase === "completed" && outcome === null && (
          <Alert type="success" message={t("uploadAdmin.messages.completed")} showIcon />
        )}

        {outcome === "partial" && !failedOnly && (
          <Alert type="info" message={t(partialMessageKey)} action={relaunchAction} showIcon />
        )}

        {failedOnly && (
          <Alert
            type="warning"
            message={t("uploadAdmin.progress.incremental.failedOnly")}
            description={status.error}
            action={relaunchAction}
            showIcon
          />
        )}

        {outcome === "complete" && (
          <Alert type="success" message={t("uploadAdmin.progress.incremental.complete")} showIcon />
        )}

        {fetchProgress && (
          <div>
            <Text strong>{t("uploadAdmin.progress.fetchProgress")}</Text>
            <Progress
              percent={fetchPercent}
              status={status.phase === "failed" ? "exception" : fetchPercent === 100 ? "success" : "active"}
              format={() => fetchText}
              aria-label={t("uploadAdmin.progress.fetchProgress")}
              aria-valuetext={fetchText}
            />
          </div>
        )}

        {ingestProgress && (
          <div>
            <Text strong>{t("uploadAdmin.progress.ingestProgress")}</Text>
            <Progress
              percent={ingestPercent}
              status={ingestBarStatus(status.phase, ingestPercent, ingestFailed)}
              format={() => ingestText}
              aria-label={t("uploadAdmin.progress.ingestProgress")}
              aria-valuetext={ingestValueText}
            />
          </div>
        )}

        {status.sync_progress && (
          <IncrementalSyncDetails
            sync={status.sync_progress}
            phase={status.phase}
            failed={ingestProgress?.failed ?? 0}
            failures={status.phase === "completed" && !failedOnly ? status.error : null}
          />
        )}

        {status.stale_removal && removalNotice && (
          <StaleRemovalAlert removal={status.stale_removal} notice={removalNotice} />
        )}

        {status.files && status.files.length > 0 && (
          <DataTable
            columns={fileColumns}
            data={status.files}
            rowKey={(file) => file.blob_name}
            size="small"
            pagination={status.files.length > 10 ? { pageSize: 10 } : false}
          />
        )}

        {isTerminal && (
          <div className="flex gap-2">
            <Button variant="primary" onClick={onNewUpload}>
              {t("uploadAdmin.actions.newUpload")}
            </Button>
            {canRelaunch && !relaunchInAlert && (
              <Button onClick={relaunch} data-testid="relaunch-sync-button">
                {t("uploadAdmin.progress.incremental.relaunch")}
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
